import React, { useState, useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { registerLocale } from 'react-datepicker'
import fr from 'date-fns/locale/fr'
import es from 'date-fns/locale/es'
import enGB from 'date-fns/locale/en-GB'

import NavigationBar from '../components/NavigationBar'
import Footer from '../components/Footer/Footer'

import PoiTypeMultiSelect from './../components/PoiTypeMultiSelect'
import DisplayPois from './../components/PointsOfInterest/DisplayPois'
import GoogleMap from './../components/PointsOfInterest/GoogleMap'
import { MANAGE_EVENT_NEW_PATH } from '../config/routes'
import { AuthContext } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import PageHeroTitle from '../components/PageHeroTitle'
import BottomImage from '../components/BottomImage'

import { usePois } from '../contexts/PoisContext'

registerLocale('fr', fr)
registerLocale('es', es)
registerLocale('en', enGB)

const Events = () => {
  const { t } = useTranslation()
  const { activeLanguage } = useLanguage()
  const { isAdmin } = useContext(AuthContext)
  const { pois: allPois, poiTypes: allPoiTypes } = usePois()
  const pageName = 'EVENTS'

  // On filtre dans le composant que les poiTypes isEvent = true
  const poiTypes = allPoiTypes.filter((pt) => pt.isEvent === true)

  const [selectedPoiTypes, setSelectedPoiTypes] = useState(() => {
    const savedSelectedTypes = localStorage.getItem('selectedPoiTypesEvents')
    return savedSelectedTypes ? JSON.parse(savedSelectedTypes) : []
  })

  const [filterByDate, setFilterByDate] = useState(false)

  const [startDate, setStartDate] = useState(() => {
    const savedStart = localStorage.getItem('eventsStartDate')
    return savedStart
      ? new Date(savedStart)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  })

  const [endDate, setEndDate] = useState(() => {
    const savedEnd = localStorage.getItem('eventsEndDate')
    if (savedEnd) return new Date(savedEnd)

    const futureDate = new Date()
    futureDate.setMonth(futureDate.getMonth() + 11)
    futureDate.setDate(0)
    futureDate.setHours(23, 59, 59, 999)
    return futureDate
  })

  useEffect(() => {
    localStorage.setItem(
      'selectedPoiTypesEvents',
      JSON.stringify(selectedPoiTypes),
    )
  }, [selectedPoiTypes])

  useEffect(() => {
    if (startDate)
      localStorage.setItem('eventsStartDate', startDate.toISOString())
  }, [startDate])

  useEffect(() => {
    if (endDate) localStorage.setItem('eventsEndDate', endDate.toISOString())
  }, [endDate])

  const handleButtonClick = (name) => {
    setSelectedPoiTypes((prev) =>
      prev.includes(name)
        ? prev.filter((item) => item !== name)
        : [...prev, name],
    )
  }

  const toggleFilterByDate = (value) => {
    setFilterByDate(value)
  }

  // Filtrage des POIs événements
  let filteredPois = allPois.filter((poi) => poi.poiType?.isEvent === true)

  if (!isAdmin) {
    filteredPois = filteredPois.filter((poi) => poi.status !== 'DRAFT')
  }

  filteredPois = filteredPois.filter((poi) => {
    if (selectedPoiTypes.length === 0) return true
    return selectedPoiTypes.includes(poi.poiType.name)
  })

  if (filterByDate) {
    filteredPois = filteredPois.filter((poi) => {
      if (!poi.startDate) return false

      const poiStartDate = new Date(poi.startDate)
      const poiEndDate = poi.endDate ? new Date(poi.endDate) : poiStartDate

      return poiStartDate <= endDate && poiEndDate >= startDate
    })
  }

  return (
    <div>
      <NavigationBar />

      <PageHeroTitle
        title={t('EVENTS')}
        description={t('EVENTS_DESCRIPTION')}
      />

      <div className="p-3">
        {isAdmin && (
          <Link
            to={MANAGE_EVENT_NEW_PATH}
            className="btn btn-primary btn-margin-10"
          >
            {t('ADD')}
          </Link>
        )}

        <div className="block-wrapper">
          <PoiTypeMultiSelect
            poiTypes={poiTypes}
            selectedPoiTypes={selectedPoiTypes}
            handleButtonClick={handleButtonClick}
          />

          <div className="row mb-4 text-center">
            <div className="col-12 col-md-4 mb-3 mb-md-0 d-flex align-items-center justify-content-center">
              <span className="me-2">{t('FILTER_BY_DATE')}?</span>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${
                    filterByDate ? 'btn-primary' : 'btn-secondary'
                  }`}
                  onClick={() => toggleFilterByDate(true)}
                >
                  {t('YES')}
                </button>
                <button
                  type="button"
                  className={`btn ${
                    !filterByDate ? 'btn-primary' : 'btn-secondary'
                  }`}
                  onClick={() => toggleFilterByDate(false)}
                >
                  {t('NO')}
                </button>
              </div>
            </div>

            {filterByDate && (
              <>
                <div className="col-12 col-md-4 mb-3 mb-md-0">
                  <label
                    style={{ paddingRight: '10px' }}
                    className="form-label"
                  >
                    {t('MINIMAL_MONTH')}
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) =>
                      setStartDate(
                        new Date(date.getFullYear(), date.getMonth(), 1),
                      )
                    }
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    className="form-control mx-auto"
                    locale={activeLanguage}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label
                    style={{ paddingRight: '10px' }}
                    className="form-label"
                  >
                    {t('MAXIMAL_MONTH')}
                  </label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) =>
                      setEndDate(
                        new Date(
                          date.getFullYear(),
                          date.getMonth() + 1,
                          0,
                          23,
                          59,
                          59,
                          999,
                        ),
                      )
                    }
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    className="form-control mx-auto"
                    locale={activeLanguage}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <GoogleMap
          pois={filteredPois}
          selectedPoiTypes={selectedPoiTypes}
          startDate={filterByDate ? startDate : null}
          endDate={filterByDate ? endDate : null}
        />

        <DisplayPois pois={filteredPois} />

        <hr />
        <BottomImage name={pageName} />
      </div>

      <Footer />
    </div>
  )
}

export default Events
